import React, { useState, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

function getPreviewUrl(item) {
  if (item.type === "file" && item.previewUrl) return item.previewUrl;
  if (item.type === "url" && item.url) return item.url;
  return "";
}

function SortablePhotoItem({ id, item, index, isPrimary, isRejected, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const previewUrl = getPreviewUrl(item);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`photo-upload-item ${isDragging ? "photo-upload-item-dragging" : ""}`}
      {...attributes}
      {...listeners}
      role="button"
      tabIndex={0}
      aria-label="Drag to reorder photo"
    >
      <div className="photo-upload-drag-handle" aria-hidden="true">
        ⋮⋮
      </div>
      <img src={previewUrl} alt="" draggable={false} />
      {isRejected && (
        <span className="photo-upload-rejected-badge">Rejected</span>
      )}
      {isPrimary && !isRejected && (
        <span className="photo-upload-primary-badge">Primary</span>
      )}
      <button
        type="button"
        className="photo-remove-btn"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(index);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label="Remove photo"
      >
        ×
      </button>
    </div>
  );
}

const PhotoUpload = ({
  label,
  value = [],
  onChange,
  onRemove,
  minPhotos = 0,
  maxPhotos = 5,
  rejectedIndices = [],
}) => {
  const [error, setError] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const handleFileSelect = useCallback(
    (e) => {
      const files = Array.from(e.target.files || []);
      e.target.value = "";
      if (files.length === 0) return;

      setError("");
      if (value.length + files.length > maxPhotos) {
        setError(
          `You can only add up to ${maxPhotos} photo${maxPhotos === 1 ? "" : "s"}. Remove one to add more.`
        );
        return;
      }

      const newItems = [];
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE) {
          setError("This file is too large. Please choose an image under 5 MB.");
          return;
        }
        if (!ALLOWED_TYPES.includes(file.type)) {
          setError("Please upload a photo in JPG, PNG, or WebP format.");
          return;
        }
        newItems.push({
          type: "file",
          file,
          previewUrl: URL.createObjectURL(file),
        });
      }

      onChange([...value, ...newItems]);
    },
    [value, maxPhotos, onChange]
  );

  const handleRemovePhoto = useCallback(
    (index) => {
      const item = value[index];
      if (item && item.type === "file" && item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
      if (onRemove) {
        onRemove(index);
      } else {
        const updated = value.filter((_, i) => i !== index);
        onChange(updated);
      }
    },
    [value, onChange, onRemove]
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = value.findIndex((_, i) => String(i) === String(active.id));
      const newIndex = value.findIndex((_, i) => String(i) === String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(value, oldIndex, newIndex);
      onChange(reordered);
    },
    [value, onChange]
  );

  const photoIds = value.map((_, i) => String(i));

  return (
    <div className="photo-upload-section">
      <div className="photo-upload-header">
        <label className="input-label">{label}</label>
        <span className="photo-upload-count">
          {value.length} / {maxPhotos}
          {minPhotos > 0 && ` (at least ${minPhotos} required)`}
        </span>
      </div>

      {error && (
        <div className="error-message photo-upload-error" role="alert">
          <p>{error}</p>
        </div>
      )}

      <div className="photo-upload-grid">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={photoIds}
            strategy={verticalListSortingStrategy}
          >
            {value.map((item, index) => (
              <SortablePhotoItem
                key={`${index}-${item.type}-${item.url || item.previewUrl || index}`}
                id={String(index)}
                item={item}
                index={index}
                isPrimary={index === 0}
                isRejected={rejectedIndices.includes(index)}
                onRemove={handleRemovePhoto}
              />
            ))}
          </SortableContext>
        </DndContext>

        {value.length < maxPhotos && (
          <label className="photo-upload-placeholder">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            <div className="photo-upload-content">
              <span className="upload-icon">📷</span>
              <span>Add Photos</span>
            </div>
          </label>
        )}
      </div>

      <p className="photo-upload-hint">
        Drag to reorder. First photo is primary. Max 5 MB per image. JPG, PNG, or
        WebP.
      </p>
    </div>
  );
};

export default PhotoUpload;
export { getPreviewUrl };
